import React from 'react';
import { Box, Button, Typography, Grid2 } from '@mui/material';
import { SelectPricePoliciesVariant, SelectedPoliciesDisplay, useSelectPricePolicies } from './index';
import { ListPricePoliciesUseCase } from '../../../../Usecase/PricePolicies/list-price-policies-usecase';
import { PricePoliciesApiRemoteService } from '../../../../Services/api/price-policies/price-policies-service';
import { IPricePoliciesEntity } from '../../../../Services/api/price-policies/price-policies.interface';
import api from '../../../../Services/api';

interface IPricePoliciesFormIntegrationProps {
    selectedPolicies?: IPricePoliciesEntity[];
    onPoliciesChange?: (policies: IPricePoliciesEntity[]) => void;
    disabled?: boolean;
    variant?: 'compact' | 'detailed';
    showTitle?: boolean;
    buttonText?: string;
    multiple?: boolean;
}

/**
 * Componente integrado para formulários que precisam de seleção de políticas de preço
 * 
 * Este componente encapsula toda a lógica necessária para selecionar e exibir
 * políticas de preço em formulários, sem necessidade de configuração externa.
 */
const PricePoliciesFormIntegration: React.FC<IPricePoliciesFormIntegrationProps> = ({
    selectedPolicies = [],
    onPoliciesChange,
    disabled = false,
    variant = 'detailed',
    showTitle = true,
    buttonText = 'Selecionar Políticas de Preço',
    multiple = true
}) => {
    // Instâncias dos serviços (podem ser movidas para um contexto global)
    const pricePoliciesService = new PricePoliciesApiRemoteService(api);
    const listPricePoliciesUseCase = new ListPricePoliciesUseCase(pricePoliciesService);

    // Hook para gerenciar o estado da modal
    const {
        isOpen,
        selectedPolicies: internalSelectedPolicies,
        openModal,
        closeModal,
        handleSelect,
        removePolicy
    } = useSelectPricePolicies({
        multiple,
        initialSelected: selectedPolicies,
        onSelect: (policies) => {
            onPoliciesChange?.(policies);
        }
    });

    // Sincronizar com props externas
    React.useEffect(() => {
        if (selectedPolicies.length !== internalSelectedPolicies.length || 
            !selectedPolicies.every(p => internalSelectedPolicies.some(ip => ip.id === p.id))) {
            handleSelect(selectedPolicies);
        }
    }, [selectedPolicies]);

    const handleRemovePolicy = (policyId: string) => {
        removePolicy(policyId);
        const updatedPolicies = internalSelectedPolicies.filter(p => p.id !== policyId);
        onPoliciesChange?.(updatedPolicies);
    };

    return (
        <Box>
            {showTitle && (
                <Typography variant="h6" gutterBottom>
                    Políticas de Preço
                </Typography>
            )}

            {/* Botão para abrir modal */}
            <Box mb={2}>
                <Button
                    variant="contained"
                    onClick={openModal}
                    disabled={disabled}
                    sx={{ minWidth: '200px', color: '#FFF' }}
                >
                    {buttonText}
                    {internalSelectedPolicies.length > 0 && ` (${internalSelectedPolicies.length})`}
                </Button>
            </Box>

            {/* Exibição das políticas selecionadas */}
            <SelectedPoliciesDisplay
                selectedPolicies={internalSelectedPolicies}
                onRemovePolicy={handleRemovePolicy}
                showRemoveButton={!disabled}
                variant={variant}
                title=""
            />

            {/* Modal de seleção */}
            {isOpen && (
                <SelectPricePoliciesVariant
                    onClose={closeModal}
                    onSelect={handleSelect}
                    listPricePoliciesUseCase={listPricePoliciesUseCase}
                    selectedPolicies={internalSelectedPolicies}
                    multiple={multiple}
                />
            )}
        </Box>
    );
};

export default PricePoliciesFormIntegration;

/**
 * EXEMPLO DE USO EM FORMULÁRIO:
 * 
 * ```tsx
 * import PricePoliciesFormIntegration from './Components/Modals/variants/select-price-policies/price-policies-form-integration';
 * 
 * const ProductForm = () => {
 *   const [formData, setFormData] = useState({
 *     name: '',
 *     price: '',
 *     pricePolicies: []
 *   });
 * 
 *   return (
 *     <form>
 *       <Input 
 *         label="Nome do Produto"
 *         value={formData.name}
 *         onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
 *       />
 *       
 *       <PricePoliciesFormIntegration
 *         selectedPolicies={formData.pricePolicies}
 *         onPoliciesChange={(policies) => 
 *           setFormData(prev => ({ ...prev, pricePolicies: policies }))
 *         }
 *         variant="compact"
 *         multiple={true}
 *       />
 *     </form>
 *   );
 * };
 * ```
 */
